import { useRef } from "react";
import { PieChart as PieIcon, Store } from "lucide-react";
import { ChartDownloadButton } from "@/components/common/ChartDownloadButton";
import { EmptyState } from "@/components/common/ui-bits";
import { money } from "@/lib/format";

export const PALETTE = ["#F43F5E", "#00A8E1", "#FFC72C", "#111111", "#10B981", "#8B5CF6", "#64748B", "#EC4899", "#84CC16"];

export function CategoryBreakdown({ rows, filename = "DaLilShop_ExpensesByCategory.png" }) {
  const ref = useRef(null);
  return (
    <div ref={ref} className="surface p-5 sm:p-6 h-full fade-up" data-testid="category-breakdown">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="font-heading text-lg font-semibold">Expenses by category</h3>
        <ChartDownloadButton targetRef={ref} filename={filename} testId="category-breakdown-download-png" />
      </div>
      {rows.length === 0 ? <EmptyState icon={PieIcon} text="No data for this period" testId="category-breakdown-empty" /> : (
        <div className="space-y-4">
          {rows.map((r, i) => (
            <div key={r.category_id} data-testid={`category-breakdown-row-${r.category_id}`}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="font-medium truncate">{r.name}{r.is_archived ? " (archived)" : ""}</span>
                <span className="font-num font-semibold whitespace-nowrap">{money(r.total_cents)}</span>
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <div className="h-2 flex-1 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${r.percent}%`, background: PALETTE[i % PALETTE.length] }} />
                </div>
                <span className="text-[11px] text-muted-foreground w-10 text-right font-num">{r.percent}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function VendorTotals({ rows }) {
  return (
    <div className="surface p-5 sm:p-6 h-full fade-up" data-testid="vendor-totals">
      <h3 className="font-heading text-lg font-semibold mb-1">Spending by vendor / payee</h3>
      <p className="text-xs text-muted-foreground mb-4">All expenses grouped by who was paid</p>
      {rows.length === 0 ? <EmptyState icon={Store} text="No data for this period" testId="vendor-totals-empty" /> : (
        <ol className="divide-y divide-border">
          {rows.slice(0, 10).map((v, i) => (
            <li key={v.vendor} className="flex items-center gap-3 py-2.5" data-testid={`vendor-row-${v.vendor.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <span className="h-6 w-6 shrink-0 rounded-md bg-secondary grid place-items-center text-[11px] font-semibold text-muted-foreground">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{v.vendor}</div>
                <div className="text-[11px] text-muted-foreground">{v.count} {v.count === 1 ? "payment" : "payments"}</div>
              </div>
              <span className="font-num text-sm font-semibold text-rose-600 whitespace-nowrap">{money(v.total_cents)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
